import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { resolveCtaUrl } from "../../lib/cta";

export default function Hero({ data, content }) {
  const ctaHref = resolveCtaUrl(content);

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative overflow-hidden bg-alabaster"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-24 lg:pt-24 lg:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-6"
        >
          <p className="overline mb-6" data-testid="hero-overline">{data.overline}</p>
          <h1
            data-testid="hero-title"
            className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.05] tracking-tight text-onyx"
          >
            {data.title}
            <br />
            <span className="italic text-onyx/90">{data.titleHighlight}</span>
          </h1>
          <p
            data-testid="hero-subtitle"
            className="mt-8 text-base sm:text-lg text-onyx/70 leading-relaxed max-w-xl"
          >
            {data.subtitle}
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href={ctaHref}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-3 px-8 py-4 bg-onyx text-alabaster text-xs tracking-[0.25em] uppercase font-semibold hover:bg-gold hover:text-onyx transition-all duration-300"
            >
              {data.ctaPrimary}
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </a>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => document.getElementById("method")?.scrollIntoView({ behavior: "smooth" })}
              className="inline-flex items-center gap-2 px-2 py-4 text-xs tracking-[0.25em] uppercase font-semibold text-onyx border-b border-onyx hover:border-gold hover:text-gold transition-colors"
            >
              {data.ctaSecondary}
            </button>
          </div>
          <div className="mt-12 flex items-center gap-6">
            <div className="flex -space-x-3">
              {[
                "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&q=80",
                "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&q=80",
                "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=200&q=80",
              ].map((u, i) => (
                <img key={i} src={u} alt="aluna" className="w-10 h-10 rounded-full border-2 border-alabaster object-cover" />
              ))}
            </div>
            <div>
              <p className="text-sm text-onyx font-semibold">+500 alunas transformadas</p>
              <p className="text-xs text-onyx/60 tracking-wide">⭐ 4.9/5 de satisfação</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="lg:col-span-6 relative"
        >
          <div className="relative aspect-[4/5] overflow-hidden">
            <img
              data-testid="hero-image"
              src={data.image}
              alt="Personal Trainer"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-onyx/20 via-transparent to-transparent" />
          </div>
          {/* Floating accent stat */}
          <div className="absolute -bottom-6 -left-6 hidden md:block bg-onyx text-alabaster px-6 py-5 max-w-[220px]">
            <p className="text-3xl font-serif text-gold">90</p>
            <p className="text-xs tracking-[0.2em] uppercase mt-1">Dias para transformar</p>
          </div>
          <div className="absolute -top-4 -right-4 hidden md:block border border-gold bg-alabaster px-5 py-4">
            <p className="text-xs tracking-[0.2em] uppercase text-onyx/60">Método</p>
            <p className="font-serif text-xl text-onyx">100% Online</p>
          </div>
        </motion.div>
      </div>

      {/* Decorative line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
    </section>
  );
}
